import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, IconButton, Link, Paper, Stack, Typography,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit } from '@mui/icons-material';
import { DataGridPremium, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { profitShareService, type MonthRow, type ProfitShareLine } from '../../services/profit-share.service';
import { useAuthStore } from '../../stores/auth.store';
import { errorMessage } from '../../utils/errorMessage';
import MonthsTable from './MonthsTable';
import { ConfirmDialog, LineDialog, PaidDialog, TermsDialog } from './dialogs';
import { KIND_LABEL, money, monthLabel, reportLink, shiftMonth, thisMonth } from './profit-share.format';

type Pending =
  | { kind: 'terms' }
  | { kind: 'line'; line: ProfitShareLine | null }
  | { kind: 'deleteLine'; line: ProfitShareLine }
  | { kind: 'deleteTerms'; id: string; month: string }
  | { kind: 'close' | 'reopen' | 'unpaid' | 'paid'; month: MonthRow };

export default function ProfitShareWorkspacePage() {
  const { workspaceId = '' } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['profit-share', 'workspace', workspaceId],
    queryFn: () => profitShareService.workspace(workspaceId),
    enabled: isAdmin && !!workspaceId,
  });

  const lastClosed = data?.months.find((m) => m.status !== 'estimate')?.month;
  const firstOpen = lastClosed ? shiftMonth(lastClosed, 1) : undefined;
  const isOpen = (m: string) => !lastClosed || m > lastClosed;
  const currentShare = data?.terms.filter((t) => t.effective_month <= thisMonth()).at(-1);

  const close = () => { setPending(null); setError(null); };
  const act = useMutation({
    mutationFn: async (run: () => Promise<unknown>) => run(),
    onSuccess: () => {
      close();
      qc.invalidateQueries({ queryKey: ['profit-share'] });
    },
    onError: (e) => setError(errorMessage(e, 'Something went wrong')),
  });
  const run = (fn: () => Promise<unknown>, done?: string) => act.mutate(async () => {
    await fn();
    if (done) enqueueSnackbar(done, { variant: 'success' });
  });

  const lineColumns: GridColDef<ProfitShareLine>[] = useMemo(() => [
    { field: 'month', headerName: 'Month', width: 100, valueFormatter: (v: string) => monthLabel(v) },
    { field: 'kind', headerName: 'Kind', width: 160, renderCell: ({ row }) => <Chip size="small" variant="outlined" label={KIND_LABEL[row.kind]} color={row.kind === 'waiver' ? 'secondary' : 'default'} /> },
    { field: 'amount', headerName: 'Amount', type: 'number', width: 120, renderCell: ({ value }) => <Box sx={{ color: value < 0 ? 'error.main' : undefined }}>{money(value)}</Box> },
    { field: 'note', headerName: 'Note', flex: 1, minWidth: 200 },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }: GridRenderCellParams<ProfitShareLine>) => (isOpen(row.month) ? (
        <Stack direction="row">
          <IconButton size="small" aria-label="Edit line" onClick={() => setPending({ kind: 'line', line: row })}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" aria-label="Delete line" onClick={() => setPending({ kind: 'deleteLine', line: row })}><Delete fontSize="small" /></IconButton>
        </Stack>
      ) : <Typography variant="caption" color="text.disabled">closed</Typography>),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [lastClosed]);

  if (!isAdmin) return <Alert severity="warning">You do not have access to this page.</Alert>;
  if (loadError) return <Alert severity="error">Could not load this workspace’s profit share.</Alert>;
  if (isLoading || !data) return <Typography color="text.secondary">Loading…</Typography>;

  const p = pending;
  return (
    <Box>
      <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/profit-share')} sx={{ mb: 1 }}>Profit share</Button>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h4" fontWeight={600}>{data.workspace_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {currentShare ? `Partner share ${currentShare.share_pct}% · we keep ${100 - currentShare.share_pct}%` : 'No profit share — the company keeps all of it.'}
            {' · '}Months are UTC. <Link component={RouterLink} to={reportLink(thisMonth())}>Check against the report</Link>
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setPending({ kind: 'terms' })}>{data.terms.length ? 'Change share' : 'Set a share'}</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setPending({ kind: 'line', line: null })}>Add line</Button>
      </Box>

      {data.terms.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
          <Typography variant="caption" color="text.secondary">Share history:</Typography>
          {data.terms.map((t) => (
            <Chip key={t.id} size="small" label={`${t.share_pct}% from ${monthLabel(t.effective_month)}`} title={t.note ?? undefined}
              onDelete={isOpen(t.effective_month) ? () => setPending({ kind: 'deleteTerms', id: t.id, month: t.effective_month }) : undefined} />
          ))}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <MonthsTable months={data.months} actions={{
          close: (m) => setPending({ kind: 'close', month: m }),
          reopen: (m) => setPending({ kind: 'reopen', month: m }),
          markPaid: (m) => setPending({ kind: 'paid', month: m }),
          unmarkPaid: (m) => setPending({ kind: 'unpaid', month: m }),
        }} />
      </Paper>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Adjustments, expenses and waivers</Typography>
      {data.lines.length ? (
        <Paper variant="outlined">
          <DataGridPremium rows={[...data.lines].reverse()} columns={lineColumns} density="compact" disableRowSelectionOnClick hideFooter autoHeight />
        </Paper>
      ) : <Typography variant="body2" color="text.secondary">None yet. Add a line when a network pays differently, for an expense, or to waive a loss.</Typography>}

      <TermsDialog open={p?.kind === 'terms'} onClose={close} busy={act.isPending} error={error} firstOpenMonth={firstOpen} currentPct={currentShare?.share_pct}
        onSave={(v) => run(() => profitShareService.setTerms({ workspace_id: workspaceId, ...v, note: v.note || undefined }), 'Share saved')} />
      <LineDialog open={p?.kind === 'line'} onClose={close} busy={act.isPending} error={error}
        line={p?.kind === 'line' ? p.line : null} defaultMonth={firstOpen && firstOpen > thisMonth() ? firstOpen : thisMonth()}
        onSave={(v) => run(() => (p?.kind === 'line' && p.line
          ? profitShareService.editLine(p.line.id, v)
          : profitShareService.addLine({ workspace_id: workspaceId, ...v })), 'Line saved')} />
      <PaidDialog open={p?.kind === 'paid'} onClose={close} busy={act.isPending} error={error}
        month={p?.kind === 'paid' ? p.month.month : ''} payout={p?.kind === 'paid' ? p.month.payout : 0}
        onSave={(v) => p?.kind === 'paid' && run(() => profitShareService.markPaid(workspaceId, p.month.month, v), 'Marked paid')} />
      <ConfirmDialog open={p?.kind === 'close'} onClose={close} busy={act.isPending} error={error}
        title={p?.kind === 'close' ? `Close ${monthLabel(p.month.month)}?` : ''} confirm="Close month"
        body={p?.kind === 'close'
          ? `This freezes the month: net ${money(p.month.net)}, partner payout ${money(p.month.payout)}, carried loss ${money(p.month.carry_out)}. Later changes to tracked numbers will need a line in an open month.`
          : ''}
        onConfirm={() => p?.kind === 'close' && run(() => profitShareService.close(workspaceId, p.month.month), 'Month closed')} />
      <ConfirmDialog open={p?.kind === 'reopen'} onClose={close} busy={act.isPending} error={error}
        title={p?.kind === 'reopen' ? `Reopen ${monthLabel(p.month.month)}?` : ''} confirm="Reopen"
        body="Its numbers become a live estimate again until you close it, and its lines can be edited."
        onConfirm={() => p?.kind === 'reopen' && run(() => profitShareService.reopen(workspaceId, p.month.month), 'Month reopened')} />
      <ConfirmDialog open={p?.kind === 'unpaid'} onClose={close} busy={act.isPending} error={error}
        title="Unmark paid?" confirm="Unmark" body="The month stays closed; only the payment record is removed."
        onConfirm={() => p?.kind === 'unpaid' && run(() => profitShareService.unmarkPaid(workspaceId, p.month.month))} />
      <ConfirmDialog open={p?.kind === 'deleteLine'} onClose={close} busy={act.isPending} error={error}
        title="Delete this line?" confirm="Delete" body={p?.kind === 'deleteLine' ? `${KIND_LABEL[p.line.kind]} of ${money(p.line.amount)}: “${p.line.note}”` : ''}
        onConfirm={() => p?.kind === 'deleteLine' && run(() => profitShareService.deleteLine(p.line.id))} />
      <ConfirmDialog open={p?.kind === 'deleteTerms'} onClose={close} busy={act.isPending} error={error}
        title="Delete this share?" confirm="Delete" body={p?.kind === 'deleteTerms' ? `Months from ${monthLabel(p.month)} fall back to the previous share, or none.` : ''}
        onConfirm={() => p?.kind === 'deleteTerms' && run(() => profitShareService.deleteTerms(p.id))} />
    </Box>
  );
}
